
import { lastValueFrom, map } from "rxjs";
import { TealHealthEncounter } from "../models/encounter";
import { DraftMedicalBill, Encounter, ICategory, MedicalService, ProviderDiscount, ProviderMarkup } from "@harlowe/core";
import { BillDiscountRequest, BillMarkupRequest } from "@harlowe/core";
import { plainToInstance } from "class-transformer";
import { Injectable } from "@angular/core";

@Injectable({
    providedIn: 'root'
  })
export class TealHealthMedicalService extends MedicalService
{
    constructor
    (
    )
    {
      super();
    }
    

    override getEligibleEncountersForDraftBill<E extends Encounter>(draft:DraftMedicalBill) : Promise<Encounter[]>
    {
      return lastValueFrom
      (
        this._api.makeAPIGetCall<E[]>(this._baseUrl,`/medical/bills/drafts/${draft.id}/eligibleEncounters`,{})
        .pipe
        (
            map
            (
                e => plainToInstance(TealHealthEncounter,e)
            )
        )
      )
    }

    override getEligibleEncountersForPatient<E extends Encounter>(patientId: number) : Promise<Encounter[]>
    {
      return lastValueFrom
      (
        this._api.makeAPIGetCall<E[]>(this._baseUrl,`/parties/${patientId}/currentPlanEncounters`,{})
        .pipe
        (
            map
            (
                e => plainToInstance(TealHealthEncounter,e)
            )
        )
      )
    }

    addPracticeMarkup(ein: string, request: BillMarkupRequest): Promise<void>
    {
        return lastValueFrom(
            this._api.makeAPIPutCall<void>(this._baseUrl, `/medical/practice/${ein}/markup`, request)
                .pipe(map(() => void 0))
        );
    }

    addPracticeDiscount(ein: string, request: BillDiscountRequest): Promise<void>
    {
        return lastValueFrom(
            this._api.makeAPIPutCall<void>(this._baseUrl, `/medical/practice/${ein}/discount`, request)
                .pipe(map(() => void 0))
        );
    }

    getCategories(): Promise<ICategory[]>
    {
        return lastValueFrom(
            this._api.makeAPIGetCall<ICategory[]>(this._baseUrl, `/medical/categories`, {})
        );
    }

    getLocationMarkups(ein: string, locationId: number, npi: string): Promise<ProviderMarkup[]>
    {
        return lastValueFrom(
            this._api.makeAPIGetCall<any[]>(this._baseUrl, `/medical/practice/${ein}/locations/${locationId}/providers/${npi}/markups`, {})
                .pipe(map(results => results.map(toProviderMarkup)))
        );
    }

    getLocationDiscounts(ein: string, locationId: number, npi: string): Promise<ProviderDiscount[]>
    {
        return lastValueFrom(
            this._api.makeAPIGetCall<any[]>(this._baseUrl, `/medical/practice/${ein}/locations/${locationId}/providers/${npi}/discounts`, {})
                .pipe(map(results => results.map(toProviderDiscount)))
        );
    }

    addLocationMarkup(ein: string, locationId: number, npi: string, request: BillMarkupRequest): Promise<ProviderMarkup>
    {
        return lastValueFrom(
            this._api.makeAPIPutCall<any>(this._baseUrl, `/medical/practice/${ein}/locations/${locationId}/providers/${npi}/markup`, request)
                .pipe(map(toProviderMarkup))
        );
    }

    addLocationDiscount(ein: string, locationId: number, npi: string, request: BillDiscountRequest): Promise<ProviderDiscount>
    {
        return lastValueFrom(
            this._api.makeAPIPutCall<any>(this._baseUrl, `/medical/practice/${ein}/locations/${locationId}/providers/${npi}/discount`, request)
                .pipe(map(toProviderDiscount))
        );
    }

    deleteLocationMarkup(ein: string, locationId: number, npi: string, markupId: number): Promise<void>
    {
        return lastValueFrom(
            this._api.makeAPIDeleteCall<void>(this._baseUrl, `/medical/practice/${ein}/locations/${locationId}/providers/${npi}/markup/${markupId}`)
                .pipe(map(() => void 0))
        );
    }

    deleteLocationDiscount(ein: string, locationId: number, npi: string, discountId: number): Promise<void>
    {
        return lastValueFrom(
            this._api.makeAPIDeleteCall<void>(this._baseUrl, `/medical/practice/${ein}/locations/${locationId}/providers/${npi}/discount/${discountId}`)
                .pipe(map(() => void 0))
        );
    }
}

function toProviderMarkup(r: any): ProviderMarkup
{
    return new ProviderMarkup(r.id, r.markupType, r.amount, r.category, r.active, r.effectiveDate, r.expirationDate);
}

function toProviderDiscount(r: any): ProviderDiscount
{
    return new ProviderDiscount(r.id, r.discountType, r.amount, r.category, r.active, r.effectiveDate, r.expirationDate);
}